<script setup lang="ts">
import { NCarousel } from 'naive-ui'
import { useAttrs } from 'vue'

const props = withDefaults(defineProps<{
  autoplay?: boolean
  interval?: number
  direction?: 'horizontal' | 'vertical'
  dotType?: 'dot' | 'line'
  dotPlacement?: 'top' | 'bottom' | 'left' | 'right'
  showDots?: boolean | 'hover'
  showArrow?: boolean | 'hover'
  arrowPlacement?: 'inside' | 'outside'
  trigger?: 'click' | 'hover'
  effect?: 'slide' | 'fade' | 'card'
  slidesPerView?: number | 'auto'
  spaceBetween?: number
  centeredSlides?: boolean
  loop?: boolean
  mousewheel?: boolean
  keyboard?: boolean
  draggable?: boolean
  touchable?: boolean
  onUpdateCurrentIndex?: (currentIndex: number, lastIndex: number) => void
  onUpdateCurrentSlide?: (currentSlide: number, lastSlide: number) => void
}>(), {
  autoplay: false,
  interval: 5000,
  direction: 'horizontal',
  dotType: 'dot',
  dotPlacement: 'bottom',
  showDots: true,
  showArrow: 'hover',
  arrowPlacement: 'inside',
  trigger: 'click',
  effect: 'slide',
  slidesPerView: 1,
  spaceBetween: 0,
  centeredSlides: false,
  loop: false,
  mousewheel: false,
  keyboard: false,
  draggable: true,
  touchable: true,
})

const emit = defineEmits<{
  'update:currentIndex': [currentIndex: number]
  'update:currentSlide': [currentSlide: number]
}>()

const attrs = useAttrs()

const onUpdateCurrentIndex = (currentIndex: number, lastIndex: number) => {
  emit('update:currentIndex', currentIndex)
  props.onUpdateCurrentIndex?.(currentIndex, lastIndex)
}

const onUpdateCurrentSlide = (currentSlide: number, lastSlide: number) => {
  emit('update:currentSlide', currentSlide)
  props.onUpdateCurrentSlide?.(currentSlide, lastSlide)
}
</script>

<template>
  <NCarousel
    :autoplay="autoplay"
    :interval="interval"
    :direction="direction"
    :dot-type="dotType"
    :dot-placement="dotPlacement"
    :show-dots="showDots as boolean"
    :show-arrow="showArrow as boolean"
    :arrow-placement="arrowPlacement"
    :trigger="trigger"
    :effect="effect"
    :slides-per-view="slidesPerView"
    :space-between="spaceBetween"
    :centered-slides="centeredSlides"
    :loop="loop"
    :mousewheel="mousewheel"
    :keyboard="keyboard"
    :draggable="draggable"
    :touchable="touchable"
    v-bind="attrs"
    @update:current-index="onUpdateCurrentIndex"
    @update:current-slide="onUpdateCurrentSlide"
  >
    <template #default>
      <slot />
    </template>
    <template #arrow="{ prev, next }">
      <slot name="arrow" :prev="prev" :next="next" />
    </template>
    <template #dots="{ total, currentIndex, to }">
      <slot name="dots" :total="total" :current-index="currentIndex" :to="to" />
    </template>
  </NCarousel>
</template>

<style scoped>
/* Custom styles can be added here if needed */
</style>
